import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { getConfig } from '../../services/config';
import { useMailServer } from '../../services/api/hooks/useMailServer';

const MailServerControl: React.FC = () => {
  const [managementUrl, setManagementUrl] = useState('');
  const { status, loading, switching, error, successMessage, fetchStatus, switchInstance, setError, setSuccessMessage } = useMailServer(managementUrl);

  useEffect(() => {
    const loadUrl = async () => {
      const url = await getConfig('mail_server_management_url');
      setManagementUrl(url);
    };
    loadUrl();
  }, []);

  const handleSwitch = async (action: 'upgrade' | 'downgrade') => {
    if (!status) return;

    const targetType = action === 'upgrade' ? status.upgradeType : status.downgradeType;
    const confirmed = window.confirm(
      `Are you sure you want to ${action} the mail server to ${targetType}?\n\nThis process will take 2-3 minutes as the instance will be stopped, resized, and restarted.`
    );
    
    if (!confirmed) return;

    await switchInstance(action);
  };

  if (!managementUrl) return null;

  return (
    <Card className="mb-4">
      <Card.Header as="h6" className="text-primary border-bottom pb-2">
        Mail Server Status
      </Card.Header>
      <Card.Body>
        {switching && (
          <Alert variant="info" className="mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Switching instance type... This may take 2-3 minutes. Please wait.
          </Alert>
        )}

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {successMessage && <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}
        
        {status ? (
          <div className="mb-3">
            <div className="mb-2">
              <strong>Instance ID:</strong> <code>{status.instanceId}</code>
            </div>
            <div className="mb-2">
              <strong>Current Type:</strong> <Badge bg="info">{status.currentType}</Badge>
            </div>
            <div className="mb-2">
              <strong>State:</strong> <Badge bg={status.currentState === 'running' ? 'success' : 'secondary'}>{status.currentState}</Badge>
            </div>
            {status.message && (
              <div className="mb-2">
                <small className="text-muted">{status.message}</small>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted mb-3">Click "Fetch Status" to view server information</p>
        )}

        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            onClick={fetchStatus}
            disabled={loading || switching}
          >
            {loading && <Spinner animation="border" size="sm" className="me-1" />}
            {loading ? 'Loading...' : 'Fetch Status'}
          </Button>
          
          {status?.canUpgrade && (
            <Button 
              variant="success" 
              onClick={() => handleSwitch('upgrade')}
              disabled={loading || switching}
            >
              {switching && <Spinner animation="border" size="sm" className="me-1" />}
              {switching ? 'Upgrading...' : `Upgrade to ${status.upgradeType}`}
            </Button>
          )}
          
          {status?.canDowngrade && (
            <Button 
              variant="warning" 
              onClick={() => handleSwitch('downgrade')}
              disabled={loading || switching}
            >
              {switching && <Spinner animation="border" size="sm" className="me-1" />}
              {switching ? 'Downgrading...' : `Downgrade to ${status.downgradeType}`}
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default MailServerControl;
